import { useRef, useState } from "react";
import { api, BACKEND_URL } from "../lib/api";
import { toast } from "sonner";

/**
 * Drag-and-drop multi-image upload for gallery.
 * Calls onAdd(urls[]) when files uploaded successfully.
 */
export default function GalleryUpload({ images = [], onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);

  const upload = async (fileList) => {
    const files = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
    if (!files.length) {
      toast.error("Please select image files");
      return;
    }
    if (files.length > 30) {
      toast.error("Maximum 30 photos at once");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const r = await api.post("/uploads/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const newUrls = r.data.items.filter((it) => it.url).map((it) => `${BACKEND_URL}${it.url}`);
      const failed = r.data.items.filter((it) => it.error);
      if (failed.length) toast.warning(`${failed.length} file(s) failed`);
      if (newUrls.length) {
        onChange?.([...images, ...newUrls]);
        toast.success(`${newUrls.length} photo(s) uploaded`);
      }
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    if (e.dataTransfer.files?.length) upload(e.dataTransfer.files);
  };

  const removeAt = (i) => onChange?.(images.filter((_, x) => x !== i));
  const moveTo = (from, to) => {
    if (from === to || to < 0 || to >= images.length) return;
    const arr = [...images];
    const [item] = arr.splice(from, 1);
    arr.splice(to, 0, item);
    onChange?.(arr);
  };

  return (
    <div data-testid="gallery-upload">
      <div
        className={`relative border-2 border-dashed transition-colors ${drag ? "border-stone-900 bg-stone-100" : "border-stone-300 bg-stone-50"}`}
        onClick={() => ref.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
      >
        <input ref={ref} type="file" accept="image/*" multiple onChange={(e) => upload(e.target.files)} className="hidden" data-testid="gallery-upload-input" />
        <div className="py-10 text-center cursor-pointer">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" className="mx-auto text-stone-500">
            <path d="M12 16V4m0 0L6 10m6-6l6 6M4 20h16" />
          </svg>
          <div className="mt-3 text-sm font-medium text-stone-800">{busy ? "Uploading..." : "Drag & drop photos here"}</div>
          <div className="text-xs text-stone-500 mt-1">or click to browse · up to 30 photos · jpg/png/webp · 10MB each</div>
        </div>
      </div>

      {images.length > 0 && (
        <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="gallery-list">
          {images.map((src, i) => (
            <div key={i} className="relative aspect-square overflow-hidden bg-stone-100 group">
              <img src={src} alt="" className="w-full h-full object-cover" />
              <div className="absolute top-1 right-1 flex gap-1">
                <button onClick={() => moveTo(i, i - 1)} disabled={i === 0} className="w-6 h-6 bg-black/70 text-white text-xs flex items-center justify-center hover:bg-black disabled:opacity-30" title="Move left">‹</button>
                <button onClick={() => moveTo(i, i + 1)} disabled={i === images.length - 1} className="w-6 h-6 bg-black/70 text-white text-xs flex items-center justify-center hover:bg-black disabled:opacity-30" title="Move right">›</button>
                <button onClick={() => removeAt(i)} className="w-6 h-6 bg-black/70 text-white text-xs flex items-center justify-center hover:bg-red-600" title="Remove">×</button>
              </div>
              <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-black/60 text-white text-[10px]">{i + 1}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
